import { useEffect, useMemo, useState } from "react";
import Papa from "papaparse";
import type { TeacherSessionDraft, TeacherStudentDraft } from "../types";
import { fetchContentRows } from "../data/csv";
import {
  deleteTeacherItem,
  fetchTeacherConfig,
  fetchTeacherResults,
  saveContentCsv,
  saveTeacherItem,
  teacherLogin,
} from "../services/api";

interface Props {
  onExit: () => void;
}
type Tab = "dashboard" | "results" | "students" | "sessions" | "content";

const PROJECT_CSV_FILES = [
  ["question_files.csv", "Configuración del banco"],
  ["questions_multiple_choice.csv", "Selección múltiple"],
  ["questions_true_false.csv", "Verdadero o falso"],
  ["questions_ordering.csv", "Ordenamiento"],
  ["questions_matching.csv", "Relaciones"],
  ["questions_sources.csv", "Fuentes, imágenes y datos"],
  ["questions_development.csv", "Preguntas de desarrollo"],
  ["sources.csv", "Catálogo de fuentes"],
  ["study_support.csv", "Materiales de apoyo"],
  ["topics.csv", "Unidades y temas"],
] as const;

const EMPTY_STUDENT: TeacherStudentDraft = {
  code: "",
  studentId: "",
  firstName: "",
  lastName: "",
  course: "6°B",
  message: "",
  activeSessionId: "U3-S01",
  pendingSessionIds: "",
  profileId: "personalizado",
  questionLimit: "",
  shortPrompts: false,
  visualSupport: true,
  specificHints: true,
  reducedDistractions: false,
  shorterSources: false,
  simplifiedVocabulary: false,
  enlargedImages: false,
  preferredDifficulty: "",
  active: true,
};
const EMPTY_SESSION: TeacherSessionDraft = {
  id: "",
  title: "",
  grade: "6° básico",
  unit: "Unidad 3",
  selectedOAs: "OA 07|OA 08|OA 09",
  topics: "",
  difficulty: "Progresiva",
  vocabularyLevel: "Habitual",
  supportIntensity: "Estándar",
  questionCount: "15",
  orderMode: "random",
  completionAction: "support_only",
  supportMaterialId: "",
  learningGoal: "Explicar y aplicar los contenidos históricos de la sesión.",
  historicalSkill: "Analizar, comparar y explicar procesos históricos.",
  materials: "Cuaderno y lápiz.",
  notebookActivity:
    "Escribe un resumen con cuatro ideas importantes, dos conceptos históricos y una relación de causa y consecuencia.",
  active: true,
};

function csvEscape(value: unknown) {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}
function getField(row: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys)
    if (row[key] !== undefined) return String(row[key] ?? "");
  return "";
}
function numberField(row: Record<string, unknown>, ...keys: string[]) {
  return Number(getField(row, ...keys) || 0);
}
function parseRecord(value: unknown): Record<string, unknown> {
  if (value && typeof value === "object")
    return value as Record<string, unknown>;
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value) as unknown;
      return parsed && typeof parsed === "object"
        ? (parsed as Record<string, unknown>)
        : {};
    } catch {
      return {};
    }
  }
  return {};
}
function parseArray(value: unknown): Record<string, unknown>[] {
  if (Array.isArray(value))
    return value.filter(
      (item): item is Record<string, unknown> =>
        Boolean(item) && typeof item === "object",
    );
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value) as unknown;
      return Array.isArray(parsed)
        ? parsed.filter(
            (item): item is Record<string, unknown> =>
              Boolean(item) && typeof item === "object",
          )
        : [];
    } catch {
      return [];
    }
  }
  return [];
}
function objectField(
  row: Record<string, unknown>,
  key: string,
): Record<string, unknown> {
  return parseRecord(row[key]);
}
function studentOf(row: Record<string, unknown>) {
  return objectField(row, "student");
}
function configOf(row: Record<string, unknown>) {
  return objectField(row, "config");
}
function resultName(row: Record<string, unknown>) {
  const student = studentOf(row);
  return (
    getField(student, "name") || getField(row, "nombre", "name", "studentName")
  );
}
function resultCourse(row: Record<string, unknown>) {
  const student = studentOf(row);
  return (
    getField(student, "course") ||
    getField(row, "curso", "course", "studentCourse")
  );
}
function resultStudentId(row: Record<string, unknown>) {
  const student = studentOf(row);
  return (
    getField(student, "id") ||
    getField(row, "studentId", "student_id") ||
    resultName(row)
  );
}
function resultAssignment(row: Record<string, unknown>) {
  return (
    getField(row, "assignmentId", "assignment_id") ||
    getField(configOf(row), "sessionId")
  );
}
function resultPercentage(row: Record<string, unknown>) {
  return numberField(row, "percentage", "porcentaje");
}
function resultDifficulty(row: Record<string, unknown>) {
  return (
    getField(row, "difficulty", "dificultad") ||
    getField(configOf(row), "difficulty")
  );
}
function resultVocabulary(row: Record<string, unknown>) {
  return (
    getField(row, "vocabularyLevel", "vocabulary_level", "nivel_vocabulario") ||
    getField(configOf(row), "vocabularyLevel")
  );
}
function resultSupport(row: Record<string, unknown>) {
  return (
    getField(
      row,
      "supportIntensity",
      "support_intensity",
      "intensidad_apoyo",
    ) || getField(configOf(row), "supportIntensity")
  );
}
function resultWeakest(row: Record<string, unknown>) {
  return getField(row, "weakestTopic", "tema_refuerzo", "weakest_topic");
}
function resultDate(row: Record<string, unknown>) {
  const value = getField(row, "timestamp", "fecha");
  return value ? new Date(value).toLocaleString("es-CL") : "";
}
function rowBool(row: Record<string, unknown>, ...keys: string[]) {
  const value = getField(row, ...keys).toLowerCase();
  return !value || ["true", "sí", "si", "1"].includes(value);
}

function sessionDraftFromRow(
  row: Record<string, unknown>,
): TeacherSessionDraft {
  return {
    id: getField(row, "id"),
    title: getField(row, "title"),
    grade: getField(row, "grade") || "6° básico",
    unit: getField(row, "unit") || "Unidad 3",
    selectedOAs: getField(row, "selectedOAs", "selected_oas"),
    topics: getField(row, "topics"),
    difficulty: (getField(row, "difficulty") ||
      "Progresiva") as TeacherSessionDraft["difficulty"],
    vocabularyLevel: (getField(row, "vocabularyLevel", "vocabulary_level") ||
      "Habitual") as TeacherSessionDraft["vocabularyLevel"],
    supportIntensity: (getField(row, "supportIntensity", "support_intensity") ||
      "Estándar") as TeacherSessionDraft["supportIntensity"],
    questionCount: getField(row, "questionCount", "question_count") || "15",
    orderMode: (getField(row, "orderMode", "order_mode") ||
      "random") as TeacherSessionDraft["orderMode"],
    completionAction: (getField(row, "completionAction", "completion_action") ||
      "support_only") as TeacherSessionDraft["completionAction"],
    supportMaterialId: getField(
      row,
      "supportMaterialId",
      "support_material_id",
    ),
    notebookActivity: getField(row, "notebookActivity", "notebook_activity"),
    learningGoal: getField(row, "learningGoal", "learning_goal"),
    historicalSkill: getField(row, "historicalSkill", "historical_skill"),
    materials: getField(row, "materials") || "Cuaderno y lápiz.",
    active: rowBool(row, "active"),
  };
}

function downloadCsv(name: string, rows: Record<string, unknown>[]) {
  if (!rows.length) return;
  const headers = [...new Set(rows.flatMap((row) => Object.keys(row)))];
  const csv = [
    headers.map(csvEscape).join(","),
    ...rows.map((row) =>
      headers.map((header) => csvEscape(row[header])).join(","),
    ),
  ].join("\n");
  const link = document.createElement("a");
  link.href = URL.createObjectURL(
    new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8" }),
  );
  link.download = name;
  link.click();
  URL.revokeObjectURL(link.href);
}

function validateImportedRows(
  fileName: string,
  rows: Record<string, string>[],
): string[] {
  const warnings: string[] = [];
  if (!rows.length) return ["El archivo no contiene filas."];
  const headers = Object.keys(rows[0] ?? {});
  const seen = new Set<string>();
  if (fileName.includes("question")) {
    const required = [
      "id",
      "unit",
      "oa",
      "topic",
      "type",
      "difficulty",
      "prompt",
      "correct_answer",
    ];
    required.forEach((field) => {
      if (!headers.includes(field))
        warnings.push(`Falta la columna obligatoria “${field}”.`);
    });
    rows.forEach((row, index) => {
      if (!row.id) warnings.push(`Fila ${index + 2}: falta el identificador.`);
      if (row.id && seen.has(row.id))
        warnings.push(
          `Fila ${index + 2}: el identificador ${row.id} está repetido.`,
        );
      if (row.id) seen.add(row.id);
      if (["multiple_choice", "source", "data"].includes(row.type ?? "")) {
        try {
          const options = JSON.parse(row.options || "[]") as unknown[];
          if (options.length !== 4)
            warnings.push(`Fila ${index + 2}: debe tener cuatro alternativas.`);
        } catch {
          warnings.push(
            `Fila ${index + 2}: las alternativas no tienen formato JSON válido.`,
          );
        }
      }
    });
  }
  if (fileName.includes("student")) {
    const codes = new Set<string>();
    rows.forEach((row, index) => {
      if (codes.has(row.code ?? ""))
        warnings.push(`Fila ${index + 2}: código ${row.code} repetido.`);
      codes.add(row.code ?? "");
    });
  }
  return warnings;
}

export function TeacherScreen({ onExit }: Props) {
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(
    () => sessionStorage.getItem("teacher-token") ?? "",
  );
  const [results, setResults] = useState<Record<string, unknown>[]>([]);
  const [students, setStudents] = useState<Record<string, unknown>[]>([]);
  const [sessions, setSessions] = useState<Record<string, unknown>[]>([]);
  const [supportOptions, setSupportOptions] = useState<
    Record<string, string>[]
  >([]);
  const [tab, setTab] = useState<Tab>("dashboard");
  const [courseFilter, setCourseFilter] = useState("Todos");
  const [search, setSearch] = useState("");
  const [difficultyFilter, setDifficultyFilter] = useState("Todas");
  const [vocabularyFilter, setVocabularyFilter] = useState("Todos");
  const [supportFilter, setSupportFilter] = useState("Todos");
  const [studentDraft, setStudentDraft] = useState(EMPTY_STUDENT);
  const [sessionDraft, setSessionDraft] = useState(EMPTY_SESSION);
  const [editingSessionId, setEditingSessionId] = useState("");
  const [syncingContent, setSyncingContent] = useState(false);
  const [importRows, setImportRows] = useState<Record<string, string>[]>([]);
  const [importName, setImportName] = useState("");
  const [importWarnings, setImportWarnings] = useState<string[]>([]);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(Boolean(token));

  const reload = async (activeToken = token) => {
    const [nextResults, config] = await Promise.all([
      fetchTeacherResults(activeToken),
      fetchTeacherConfig(activeToken),
    ]);
    setResults(nextResults);
    setStudents(config.students);
    setSessions(config.sessions);
  };

  useEffect(() => {
    if (!token) return;
    let active = true;
    Promise.all([fetchTeacherResults(token), fetchTeacherConfig(token)])
      .then(([nextResults, config]) => {
        if (!active) return;
        setResults(nextResults);
        setStudents(config.students);
        setSessions(config.sessions);
      })
      .catch((reason) => {
        if (active)
          setError(
            reason instanceof Error
              ? reason.message
              : "No fue posible cargar el panel.",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchContentRows("study_support.csv")
      .then((rows) => setSupportOptions(rows))
      .catch(() => setSupportOptions([]));
  }, [token]);

  const login = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    try {
      setLoading(true);
      const nextToken = await teacherLogin(password);
      sessionStorage.setItem("teacher-token", nextToken);
      setToken(nextToken);
      setPassword("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Clave incorrecta.");
    } finally {
      setLoading(false);
    }
  };

  const filteredResults = useMemo(
    () =>
      results.filter((row) => {
        const course = resultCourse(row);
        const name = resultName(row);
        return (
          (courseFilter === "Todos" || course === courseFilter) &&
          (difficultyFilter === "Todas" ||
            resultDifficulty(row) === difficultyFilter) &&
          (vocabularyFilter === "Todos" ||
            resultVocabulary(row) === vocabularyFilter) &&
          (supportFilter === "Todos" || resultSupport(row) === supportFilter) &&
          (!search || name.toLowerCase().includes(search.toLowerCase()))
        );
      }),
    [
      courseFilter,
      difficultyFilter,
      results,
      search,
      supportFilter,
      vocabularyFilter,
    ],
  );

  const courses = useMemo(
    () =>
      [
        ...new Set(
          [
            ...students.map((row) => getField(row, "course", "curso")),
            ...results.map(resultCourse),
          ].filter(Boolean),
        ),
      ].sort(),
    [results, students],
  );
  const activeStudents = students.filter((row) =>
    rowBool(row, "active", "activo"),
  );
  const average = filteredResults.length
    ? Math.round(
        filteredResults.reduce((sum, row) => sum + resultPercentage(row), 0) /
          filteredResults.length,
      )
    : 0;
  const completedStudentIds = new Set(filteredResults.map(resultStudentId));
  const filteredStudents = activeStudents.filter(
    (row) =>
      courseFilter === "Todos" ||
      getField(row, "course", "curso") === courseFilter,
  );
  const pendingStudents = filteredStudents.filter(
    (row) =>
      !completedStudentIds.has(
        getField(row, "student_id", "studentId") ||
          `${getField(row, "first_name")} ${getField(row, "last_name")}`,
      ),
  );

  const topicErrors = new Map<string, number>();
  const questionErrors = new Map<string, number>();
  results.forEach((row) => {
    const topic = resultWeakest(row);
    if (topic) topicErrors.set(topic, (topicErrors.get(topic) ?? 0) + 1);
    const questionResults = parseArray(
      row.questionResults ?? row.question_results ?? row.resultados_preguntas,
    );
    questionResults.forEach((item) => {
      if (item.correct === false) {
        const id = getField(item, "questionId", "question_id");
        questionErrors.set(id, (questionErrors.get(id) ?? 0) + 1);
      }
    });
  });
  const hardestTopic =
    [...topicErrors.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ??
    "Sin datos suficientes";
  const lowResults = filteredResults.filter(
    (row) => resultPercentage(row) < 60,
  );

  const oaSummary = useMemo(() => {
    const data = new Map<string, { total: number; count: number }>();
    filteredResults.forEach((row) => {
      const byOA = parseRecord(row.resultsByOA ?? row.resultados_por_oa);
      Object.entries(byOA).forEach(([oa, raw]) => {
        const item = raw as Record<string, unknown>;
        const previous = data.get(oa) ?? { total: 0, count: 0 };
        previous.total += Number(item.percentage ?? 0);
        previous.count += 1;
        data.set(oa, previous);
      });
    });
    return [...data.entries()].map(([oa, item]) => ({
      oa,
      average: item.count ? Math.round(item.total / item.count) : 0,
    }));
  }, [filteredResults]);

  const sessionStatus = sessions.map((session) => {
    const id = getField(session, "id");
    const assigned = activeStudents.filter(
      (student) =>
        getField(student, "active_session_id", "activeSessionId") === id,
    );
    const completed = new Set(
      results
        .filter((row) => resultAssignment(row) === id)
        .map(resultStudentId),
    );
    return {
      id,
      title: getField(session, "title"),
      assigned: assigned.length,
      completed: assigned.filter((student) =>
        completed.has(getField(student, "student_id", "studentId")),
      ).length,
      courses:
        [
          ...new Set(
            assigned
              .map((student) => getField(student, "course"))
              .filter(Boolean),
          ),
        ].join(", ") || getField(session, "grade"),
    };
  });

  const saveStudent = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setNotice("");
    if (!/^[0-9]{4}[A-Z]?$/.test(studentDraft.code)) {
      setError(
        "El código debe contener cuatro dígitos y, solo si se repite, una letra final.",
      );
      return;
    }
    try {
      await saveTeacherItem(token, "student", studentDraft);
      setNotice("Estudiante guardado.");
      setStudentDraft(EMPTY_STUDENT);
      await reload();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "No fue posible guardar.",
      );
    }
  };

  const saveSession = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setNotice("");
    try {
      await saveTeacherItem(token, "session", sessionDraft);
      setNotice(editingSessionId ? "Sesión actualizada." : "Sesión creada.");
      setSessionDraft(EMPTY_SESSION);
      setEditingSessionId("");
      await reload();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "No fue posible guardar.",
      );
    }
  };

  const openProjectCsv = async (fileName: string) => {
    setError("");
    setNotice("");
    try {
      const rows = await fetchContentRows(fileName);
      setImportName(fileName);
      setImportRows(rows);
      setImportWarnings(validateImportedRows(fileName, rows));
      setTab("content");
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "No se pudo abrir el archivo.",
      );
    }
  };

  const duplicateImportRow = (rowIndex: number) => {
    setImportRows((current) => {
      const original = current[rowIndex];
      if (!original) return current;
      const copy = {
        ...original,
        id: original.id ? `${original.id}-ADAPTADA` : "",
      };
      const next = [...current];
      next.splice(rowIndex + 1, 0, copy);
      return next;
    });
  };

  const importCsv = (file?: File) => {
    if (!file) return;
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const rows = result.data;
        setImportName(file.name);
        setImportRows(rows);
        setImportWarnings([
          ...result.errors.map((item) => item.message),
          ...validateImportedRows(file.name, rows),
        ]);
        setError("");
      },
    });
  };

  const updateImportCell = (rowIndex: number, key: string, value: string) => {
    setImportRows((current) =>
      current.map((row, index) =>
        index === rowIndex ? { ...row, [key]: value } : row,
      ),
    );
  };

  const saveImported = async () => {
    setError("");
    setNotice("");
    const warnings = validateImportedRows(importName, importRows);
    setImportWarnings(warnings);
    if (warnings.length) {
      setError("Corrige las advertencias antes de guardar.");
      return;
    }
    try {
      await saveContentCsv(token, importName, Papa.unparse(importRows));
      setNotice(
        `El archivo ${importName} quedó guardado en Google Sheets. Recarga la página estudiantil para ver los cambios.`,
      );
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "No fue posible guardar el archivo.",
      );
    }
  };

  const editSession = (row: Record<string, unknown>) => {
    const draft = sessionDraftFromRow(row);
    setSessionDraft(draft);
    setEditingSessionId(draft.id);
    setNotice(`Editando ${draft.id}.`);
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelSessionEdit = () => {
    setSessionDraft(EMPTY_SESSION);
    setEditingSessionId("");
    setNotice("");
  };

  const toggleSession = async (row: Record<string, unknown>) => {
    setError("");
    setNotice("");
    try {
      const draft = sessionDraftFromRow(row);
      draft.active = !draft.active;
      await saveTeacherItem(token, "session", draft);
      setNotice(
        draft.active
          ? "Sesión activada."
          : "Sesión desactivada. Las y los estudiantes no podrán iniciarla.",
      );
      await reload();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "No fue posible cambiar el estado.",
      );
    }
  };

  const removeSession = async (row: Record<string, unknown>) => {
    const id = getField(row, "id");
    if (
      !id ||
      !window.confirm(
        `¿Eliminar definitivamente la sesión ${id}? Esta acción no se puede deshacer.`,
      )
    )
      return;
    setError("");
    setNotice("");
    try {
      await deleteTeacherItem(token, "session", id);
      setNotice("Sesión eliminada.");
      if (editingSessionId === id) cancelSessionEdit();
      await reload();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "No fue posible eliminar la sesión.",
      );
    }
  };

  const syncAllContent = async () => {
    if (
      !window.confirm(
        "Se crearán en Google Sheets las hojas del banco que todavía no existan. Las hojas que ya tengan contenido no serán reemplazadas.",
      )
    )
      return;
    setSyncingContent(true);
    setError("");
    setNotice("");
    try {
      let created = 0;
      let skipped = 0;
      for (const [fileName] of PROJECT_CSV_FILES) {
        const response = await fetch(`/content/${fileName}`);
        if (!response.ok) throw new Error(`No se pudo leer ${fileName}.`);
        const saved = await saveContentCsv(
          token,
          fileName,
          await response.text(),
          true,
        );
        if (saved.skipped) skipped += 1;
        else created += 1;
      }
      setNotice(
        `Banco conectado a Google Sheets: ${created} hojas creadas y ${skipped} conservadas porque ya tenían contenido.`,
      );
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "No fue posible sincronizar el banco.",
      );
    } finally {
      setSyncingContent(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("teacher-token");
    setToken("");
    setResults([]);
    setStudents([]);
    setSessions([]);
  };

  if (!token)
    return (
      <main id="main-content" className="center-page">
        <form className="teacher-login" onSubmit={login}>
          <p className="eyebrow">Sección privada</p>
          <h1>Acceso de la profesora</h1>
          <label>
            Clave docente
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
            />
          </label>
          <p className="teacher-demo">
            Utiliza la clave privada configurada para el panel docente.
          </p>
          {error && (
            <p className="error-message" role="alert">
              {error}
            </p>
          )}
          <button className="primary-button" disabled={loading}>
            {loading ? "Validando…" : "Ingresar"}
          </button>
          <button className="text-button" type="button" onClick={onExit}>
            Volver
          </button>
        </form>
      </main>
    );

  return (
    <main id="main-content" className="teacher-page v4-teacher">
      <div className="teacher-title">
        <div>
          <p className="eyebrow">Panel docente</p>
          <h1>Seguimiento y gestión</h1>
        </div>
        <div className="button-row">
          <button
            className="secondary-button"
            onClick={() =>
              downloadCsv("resultados-laboratorio.csv", filteredResults)
            }
          >
            Descargar resultados
          </button>
          <button className="secondary-button" onClick={logout}>
            Cerrar sesión
          </button>
        </div>
      </div>
      <nav className="teacher-tabs" aria-label="Secciones del panel">
        {(
          [
            ["dashboard", "Resumen"],
            ["results", "Resultados"],
            ["students", "Estudiantes"],
            ["sessions", "Sesiones"],
            ["content", "Unidades y preguntas"],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            className={tab === value ? "active" : ""}
            onClick={() => setTab(value)}
          >
            {label}
          </button>
        ))}
      </nav>
      {loading && <p>Actualizando información…</p>}
      {error && (
        <p className="error-message" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="success-message" role="status">
          {notice}
        </p>
      )}

      {(tab === "dashboard" || tab === "results") && (
        <section className="teacher-filters v4-filters">
          <label>
            Buscar estudiante
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Nombre o apellido"
            />
          </label>
          <label>
            Curso
            <select
              value={courseFilter}
              onChange={(event) => setCourseFilter(event.target.value)}
            >
              <option>Todos</option>
              {courses.map((course) => (
                <option key={course}>{course}</option>
              ))}
            </select>
          </label>
          <label>
            Dificultad
            <select
              value={difficultyFilter}
              onChange={(event) => setDifficultyFilter(event.target.value)}
            >
              <option>Todas</option>
              <option>Básica</option>
              <option>Intermedia</option>
              <option>Avanzada</option>
              <option>Progresiva</option>
            </select>
          </label>
          <label>
            Vocabulario
            <select
              value={vocabularyFilter}
              onChange={(event) => setVocabularyFilter(event.target.value)}
            >
              <option>Todos</option>
              <option>Simplificado</option>
              <option>Habitual</option>
              <option>Ampliado</option>
            </select>
          </label>
          <label>
            Apoyo
            <select
              value={supportFilter}
              onChange={(event) => setSupportFilter(event.target.value)}
            >
              <option>Todos</option>
              <option>Estándar</option>
              <option>Moderado</option>
              <option>Intensivo</option>
            </select>
          </label>
        </section>
      )}

      {tab === "dashboard" && (
        <>
          <section className="dashboard-cards">
            <article>
              <span>Sesiones registradas</span>
              <strong>{filteredResults.length}</strong>
            </article>
            <article>
              <span>Promedio del grupo</span>
              <strong>{average}%</strong>
            </article>
            <article>
              <span>Estudiantes pendientes</span>
              <strong>{pendingStudents.length}</strong>
            </article>
            <article>
              <span>Contenido con mayor dificultad</span>
              <strong className="small-stat">{hardestTopic}</strong>
            </article>
          </section>
          <section className="dashboard-layout">
            <div className="dashboard-section">
              <div className="section-heading">
                <h2>Cursos y sesiones activas</h2>
                <span>{sessionStatus.length} sesiones configuradas</span>
              </div>
              <div className="session-status-grid">
                {sessionStatus.map((item) => (
                  <article key={item.id}>
                    <div>
                      <span>{item.id}</span>
                      <h3>{item.title}</h3>
                      <p>{item.courses}</p>
                    </div>
                    <strong>
                      {item.completed}/{item.assigned}
                    </strong>
                    <small>sesiones completadas</small>
                    <progress
                      value={item.completed}
                      max={Math.max(item.assigned, 1)}
                    />
                  </article>
                ))}
              </div>
            </div>
            <div className="dashboard-section alert-section">
              <h2>Alertas de apoyo</h2>
              {lowResults.length === 0 && pendingStudents.length === 0 ? (
                <p className="success-message">
                  No hay alertas con los filtros actuales.
                </p>
              ) : (
                <ul className="alert-list">
                  {lowResults.slice(0, 6).map((row, index) => (
                    <li key={`${resultStudentId(row)}-${index}`}>
                      <strong>{resultName(row)}</strong>
                      <span>
                        {resultCourse(row)} · {resultPercentage(row)}% ·
                        reforzar {resultWeakest(row) || "contenido"}
                      </span>
                    </li>
                  ))}
                  {pendingStudents.slice(0, 6).map((row, index) => (
                    <li key={`pending-${index}`}>
                      <strong>
                        {getField(row, "first_name")}{" "}
                        {getField(row, "last_name")}
                      </strong>
                      <span>{getField(row, "course")} · sesión pendiente</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
          <section className="dashboard-layout two-dashboard">
            <div className="dashboard-section">
              <h2>Promedio por OA</h2>
              <div className="oa-dashboard">
                {oaSummary.map((item) => (
                  <div key={item.oa}>
                    <span>{item.oa}</span>
                    <div>
                      <i style={{ width: `${item.average}%` }} />
                    </div>
                    <strong>{item.average}%</strong>
                  </div>
                ))}
              </div>
            </div>
            <div className="dashboard-section">
              <h2>Preguntas con más errores</h2>
              {[...questionErrors.entries()]
                .sort((a, b) => b[1] - a[1])
                .slice(0, 8)
                .map(([id, count]) => (
                  <div className="error-question" key={id}>
                    <code>{id}</code>
                    <strong>{count} errores</strong>
                  </div>
                ))}
            </div>
          </section>
        </>
      )}

      {tab === "results" && (
        <section className="dashboard-section">
          <div className="section-heading">
            <h2>Resultados individuales</h2>
            <button
              className="secondary-button"
              onClick={() =>
                downloadCsv("resultados-filtrados.csv", filteredResults)
              }
            >
              Descargar vista
            </button>
          </div>
          <div className="table-wrapper teacher-table">
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Estudiante</th>
                  <th>Curso</th>
                  <th>Sesión</th>
                  <th>Dificultad</th>
                  <th>Vocabulario</th>
                  <th>Apoyo</th>
                  <th>Resultado</th>
                  <th>Refuerzo</th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.map((row, index) => (
                  <tr key={`${resultStudentId(row)}-${index}`}>
                    <td>{resultDate(row)}</td>
                    <td>
                      <strong>{resultName(row)}</strong>
                    </td>
                    <td>{resultCourse(row)}</td>
                    <td>{resultAssignment(row)}</td>
                    <td>{resultDifficulty(row) || "—"}</td>
                    <td>{resultVocabulary(row) || "—"}</td>
                    <td>{resultSupport(row) || "—"}</td>
                    <td>{resultPercentage(row)}%</td>
                    <td>{resultWeakest(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === "students" && (
        <section className="admin-grid">
          <form className="admin-form" onSubmit={saveStudent}>
            <h2>Crear o actualizar estudiante</h2>
            <div className="two-columns">
              <label>
                Código
                <input
                  value={studentDraft.code}
                  onChange={(e) =>
                    setStudentDraft({
                      ...studentDraft,
                      code: e.target.value
                        .replace(/[^0-9A-Za-z]/g, "")
                        .slice(0, 5)
                        .toUpperCase(),
                    })
                  }
                  required
                />
              </label>
              <label>
                ID interno
                <input
                  value={studentDraft.studentId}
                  onChange={(e) =>
                    setStudentDraft({
                      ...studentDraft,
                      studentId: e.target.value,
                    })
                  }
                  required
                />
              </label>
              <label>
                Nombre
                <input
                  value={studentDraft.firstName}
                  onChange={(e) =>
                    setStudentDraft({
                      ...studentDraft,
                      firstName: e.target.value,
                    })
                  }
                  required
                />
              </label>
              <label>
                Apellido
                <input
                  value={studentDraft.lastName}
                  onChange={(e) =>
                    setStudentDraft({
                      ...studentDraft,
                      lastName: e.target.value,
                    })
                  }
                  required
                />
              </label>
              <label>
                Curso
                <input
                  value={studentDraft.course}
                  onChange={(e) =>
                    setStudentDraft({ ...studentDraft, course: e.target.value })
                  }
                  required
                />
              </label>
              <label>
                Sesión activa
                <input
                  value={studentDraft.activeSessionId}
                  onChange={(e) =>
                    setStudentDraft({
                      ...studentDraft,
                      activeSessionId: e.target.value,
                    })
                  }
                  required
                />
              </label>
            </div>
            <label>
              Mensaje personalizado{" "}
              <small>
                Variables disponibles: {"{nombre}"}, {"{apellido}"}, {"{curso}"}
                , {"{tema}"}, {"{unidad}"}, {"{objetivo}"}
              </small>
              <textarea
                value={studentDraft.message}
                onChange={(e) =>
                  setStudentDraft({ ...studentDraft, message: e.target.value })
                }
                rows={3}
              />
            </label>
            <label>
              Sesiones pendientes, separadas por |
              <input
                value={studentDraft.pendingSessionIds}
                onChange={(e) =>
                  setStudentDraft({
                    ...studentDraft,
                    pendingSessionIds: e.target.value,
                  })
                }
              />
            </label>
            <fieldset>
              <legend>Apoyos individuales</legend>
              <div className="support-check-grid">
                <label>
                  <input
                    type="checkbox"
                    checked={studentDraft.shortPrompts}
                    onChange={(e) =>
                      setStudentDraft({
                        ...studentDraft,
                        shortPrompts: e.target.checked,
                      })
                    }
                  />{" "}
                  Enunciados breves
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={studentDraft.visualSupport}
                    onChange={(e) =>
                      setStudentDraft({
                        ...studentDraft,
                        visualSupport: e.target.checked,
                      })
                    }
                  />{" "}
                  Apoyo visual
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={studentDraft.specificHints}
                    onChange={(e) =>
                      setStudentDraft({
                        ...studentDraft,
                        specificHints: e.target.checked,
                      })
                    }
                  />{" "}
                  Pistas específicas
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={studentDraft.reducedDistractions}
                    onChange={(e) =>
                      setStudentDraft({
                        ...studentDraft,
                        reducedDistractions: e.target.checked,
                      })
                    }
                  />{" "}
                  Menos información en pantalla
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={studentDraft.shorterSources}
                    onChange={(e) =>
                      setStudentDraft({
                        ...studentDraft,
                        shorterSources: e.target.checked,
                      })
                    }
                  />{" "}
                  Fuentes más breves
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={studentDraft.simplifiedVocabulary}
                    onChange={(e) =>
                      setStudentDraft({
                        ...studentDraft,
                        simplifiedVocabulary: e.target.checked,
                      })
                    }
                  />{" "}
                  Vocabulario simplificado
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={studentDraft.enlargedImages}
                    onChange={(e) =>
                      setStudentDraft({
                        ...studentDraft,
                        enlargedImages: e.target.checked,
                      })
                    }
                  />{" "}
                  Imágenes ampliadas
                </label>
              </div>
              <div className="two-columns">
                <label>
                  Límite de preguntas
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={studentDraft.questionLimit}
                    onChange={(e) =>
                      setStudentDraft({
                        ...studentDraft,
                        questionLimit: e.target.value,
                      })
                    }
                  />
                </label>
                <label>
                  Dificultad preferida
                  <select
                    value={studentDraft.preferredDifficulty}
                    onChange={(e) =>
                      setStudentDraft({
                        ...studentDraft,
                        preferredDifficulty: e.target
                          .value as TeacherStudentDraft["preferredDifficulty"],
                      })
                    }
                  >
                    <option value="">Según sesión</option>
                    <option>Básica</option>
                    <option>Intermedia</option>
                    <option>Avanzada</option>
                    <option>Progresiva</option>
                  </select>
                </label>
              </div>
            </fieldset>
            <button className="primary-button">Guardar estudiante</button>
          </form>
          <div className="admin-list">
            <div className="section-heading">
              <h2>Estudiantes configurados</h2>
              <button
                className="secondary-button"
                onClick={() => downloadCsv("estudiantes.csv", students)}
              >
                Descargar CSV
              </button>
            </div>
            {students.slice(0, 100).map((row, index) => (
              <article key={`${getField(row, "code")}-${index}`}>
                <strong>
                  {getField(row, "firstName", "first_name")}{" "}
                  {getField(row, "lastName", "last_name")}
                </strong>
                <span>
                  {getField(row, "course")} · código {getField(row, "code")} ·
                  sesión {getField(row, "activeSessionId", "active_session_id")}
                </span>
              </article>
            ))}
          </div>
        </section>
      )}

      {tab === "sessions" && (
        <section className="admin-grid">
          <form className="admin-form" onSubmit={saveSession}>
            <div className="section-heading">
              <h2>
                {editingSessionId
                  ? `Editar sesión ${editingSessionId}`
                  : "Crear sesión"}
              </h2>
              {editingSessionId && (
                <button
                  type="button"
                  className="text-button"
                  onClick={cancelSessionEdit}
                >
                  Cancelar edición
                </button>
              )}
            </div>
            <div className="two-columns">
              <label>
                ID
                <input
                  value={sessionDraft.id}
                  readOnly={Boolean(editingSessionId)}
                  onChange={(e) =>
                    setSessionDraft({ ...sessionDraft, id: e.target.value })
                  }
                  placeholder="U3-S02"
                  required
                />
              </label>
              <label>
                Título
                <input
                  value={sessionDraft.title}
                  onChange={(e) =>
                    setSessionDraft({ ...sessionDraft, title: e.target.value })
                  }
                  required
                />
              </label>
              <label>
                Curso o nivel
                <input
                  value={sessionDraft.grade}
                  onChange={(e) =>
                    setSessionDraft({ ...sessionDraft, grade: e.target.value })
                  }
                />
              </label>
              <label>
                Unidad
                <input
                  value={sessionDraft.unit}
                  onChange={(e) =>
                    setSessionDraft({ ...sessionDraft, unit: e.target.value })
                  }
                />
              </label>
              <label>
                OA separados por |
                <input
                  value={sessionDraft.selectedOAs}
                  onChange={(e) =>
                    setSessionDraft({
                      ...sessionDraft,
                      selectedOAs: e.target.value,
                    })
                  }
                />
              </label>
              <label>
                Temas separados por |
                <input
                  value={sessionDraft.topics}
                  onChange={(e) =>
                    setSessionDraft({ ...sessionDraft, topics: e.target.value })
                  }
                />
              </label>
              <label>
                Dificultad
                <select
                  value={sessionDraft.difficulty}
                  onChange={(e) =>
                    setSessionDraft({
                      ...sessionDraft,
                      difficulty: e.target
                        .value as TeacherSessionDraft["difficulty"],
                    })
                  }
                >
                  <option>Básica</option>
                  <option>Intermedia</option>
                  <option>Avanzada</option>
                  <option>Progresiva</option>
                </select>
              </label>
              <label>
                Nivel de vocabulario
                <select
                  value={sessionDraft.vocabularyLevel}
                  onChange={(e) =>
                    setSessionDraft({
                      ...sessionDraft,
                      vocabularyLevel: e.target
                        .value as TeacherSessionDraft["vocabularyLevel"],
                    })
                  }
                >
                  <option>Simplificado</option>
                  <option>Habitual</option>
                  <option>Ampliado</option>
                </select>
              </label>
              <label>
                Intensidad de apoyo
                <select
                  value={sessionDraft.supportIntensity}
                  onChange={(e) =>
                    setSessionDraft({
                      ...sessionDraft,
                      supportIntensity: e.target
                        .value as TeacherSessionDraft["supportIntensity"],
                    })
                  }
                >
                  <option>Estándar</option>
                  <option>Moderado</option>
                  <option>Intensivo</option>
                </select>
              </label>
              <label>
                Cantidad
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={sessionDraft.questionCount}
                  onChange={(e) =>
                    setSessionDraft({
                      ...sessionDraft,
                      questionCount: e.target.value,
                    })
                  }
                />
              </label>
            </div>
            <label>
              Objetivo de la sesión
              <textarea
                value={sessionDraft.learningGoal}
                onChange={(e) =>
                  setSessionDraft({
                    ...sessionDraft,
                    learningGoal: e.target.value,
                  })
                }
                rows={2}
              />
            </label>
            <label>
              Habilidad histórica
              <input
                value={sessionDraft.historicalSkill}
                onChange={(e) =>
                  setSessionDraft({
                    ...sessionDraft,
                    historicalSkill: e.target.value,
                  })
                }
              />
            </label>
            <div className="two-columns">
              <label>
                Orden de preguntas
                <select
                  value={sessionDraft.orderMode}
                  onChange={(e) =>
                    setSessionDraft({
                      ...sessionDraft,
                      orderMode: e.target
                        .value as TeacherSessionDraft["orderMode"],
                    })
                  }
                >
                  <option value="random">Aleatorio equilibrado</option>
                  <option value="progressive">Progresivo</option>
                </select>
              </label>
              <label>
                Al finalizar
                <select
                  value={sessionDraft.completionAction}
                  onChange={(e) =>
                    setSessionDraft({
                      ...sessionDraft,
                      completionAction: e.target
                        .value as TeacherSessionDraft["completionAction"],
                    })
                  }
                >
                  <option value="support_only">
                    Mostrar refuerzo y finalizar
                  </option>
                  <option value="allow_next">Permitir siguiente sesión</option>
                  <option value="teacher_authorization">
                    Esperar autorización
                  </option>
                </select>
              </label>
              <label>
                Material de apoyo
                <input
                  list="support-material-options"
                  value={sessionDraft.supportMaterialId}
                  onChange={(e) =>
                    setSessionDraft({
                      ...sessionDraft,
                      supportMaterialId: e.target.value,
                    })
                  }
                  placeholder="Selecciona o escribe un ID"
                />
                <datalist id="support-material-options">
                  {supportOptions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.title} · {item.difficulty} · {item.vocabulary_level}{" "}
                      · {item.support_intensity}
                    </option>
                  ))}
                </datalist>
              </label>
              <label>
                Materiales
                <input
                  value={sessionDraft.materials}
                  onChange={(e) =>
                    setSessionDraft({
                      ...sessionDraft,
                      materials: e.target.value,
                    })
                  }
                />
              </label>
            </div>
            <label>
              Actividad de cuaderno
              <textarea
                value={sessionDraft.notebookActivity}
                onChange={(e) =>
                  setSessionDraft({
                    ...sessionDraft,
                    notebookActivity: e.target.value,
                  })
                }
                rows={3}
              />
            </label>
            <label className="inline-check">
              <input
                type="checkbox"
                checked={sessionDraft.active}
                onChange={(e) =>
                  setSessionDraft({ ...sessionDraft, active: e.target.checked })
                }
              />{" "}
              Sesión activa y disponible
            </label>
            <button className="primary-button">
              {editingSessionId ? "Guardar cambios" : "Crear sesión"}
            </button>
          </form>
          <div className="admin-list">
            <div className="section-heading">
              <h2>Sesiones</h2>
              <button
                className="secondary-button"
                onClick={() => downloadCsv("sesiones.csv", sessions)}
              >
                Descargar CSV
              </button>
            </div>
            {sessions.map((row, index) => (
              <article
                className="editable-admin-item"
                key={`${getField(row, "id")}-${index}`}
              >
                <div>
                  <strong>{getField(row, "title")}</strong>
                  <span>
                    {getField(row, "id")} · {getField(row, "unit")} ·{" "}
                    {getField(row, "difficulty")} ·{" "}
                    {getField(row, "vocabularyLevel", "vocabulary_level") ||
                      "Habitual"}{" "}
                    ·{" "}
                    {getField(row, "supportIntensity", "support_intensity") ||
                      "Estándar"}{" "}
                    · {getField(row, "questionCount", "question_count")}{" "}
                    preguntas ·{" "}
                    {rowBool(row, "active") ? "Activa" : "Desactivada"}
                  </span>
                </div>
                <div className="admin-item-actions">
                  <button
                    type="button"
                    className="mini-action"
                    onClick={() => editSession(row)}
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    className="mini-action"
                    onClick={() => toggleSession(row)}
                  >
                    {rowBool(row, "active") ? "Desactivar" : "Activar"}
                  </button>
                  <button
                    type="button"
                    className="mini-action danger-action"
                    onClick={() => removeSession(row)}
                  >
                    Eliminar
                  </button>
                </div>
              </article>
            ))}
            <p className="paper-note">
              Por seguridad, una sesión asignada a estudiantes no se puede
              eliminar hasta quitarla de sus columnas{" "}
              <code>active_session_id</code> y <code>pending_session_ids</code>.
              Sí puedes desactivarla de inmediato.
            </p>
          </div>
        </section>
      )}

      {tab === "content" && (
        <section className="content-admin v4-content-admin">
          <div className="section-heading">
            <div>
              <h2>Editar preguntas, materiales y unidades</h2>
              <p>
                El banco puede administrarse directamente desde Google Sheets.
                Los cambios se cargan al recargar la página, sin volver a
                publicar Netlify.
              </p>
            </div>
            <button
              type="button"
              className="primary-button"
              disabled={syncingContent}
              onClick={syncAllContent}
            >
              {syncingContent
                ? "Conectando…"
                : "Crear hojas del banco en Google Sheets"}
            </button>
          </div>
          <div className="paper-note">
            <strong>Uso recomendado:</strong> presiona el botón una sola vez. Se
            crearán las hojas que falten y no se reemplazarán las que ya tengan
            contenido. Después puedes editar las celdas directamente en tu
            planilla.
          </div>
          <div className="content-file-grid">
            {PROJECT_CSV_FILES.map(([fileName, label]) => (
              <button
                type="button"
                className="content-file-button"
                key={fileName}
                onClick={() => openProjectCsv(fileName)}
              >
                <strong>{label}</strong>
                <span>{fileName}</span>
              </button>
            ))}
          </div>
          <h3>Plantillas descargables</h3>
          <div className="template-links">
            <a href="/templates/PLANTILLA_PREGUNTAS.csv" download>
              Preguntas
            </a>
            <a href="/templates/PLANTILLA_ESTUDIANTES.csv" download>
              Estudiantes
            </a>
            <a href="/templates/PLANTILLA_SESIONES.csv" download>
              Sesiones
            </a>
            <a href="/templates/PLANTILLA_MATERIALES_APOYO.csv" download>
              Materiales de apoyo
            </a>
            <a href="/templates/PLANTILLA_PERFILES_APOYO.csv" download>
              Perfiles de apoyo
            </a>
            <a href="/templates/PLANTILLA_PREGUNTAS_DESARROLLO.csv" download>
              Desarrollo y conceptos
            </a>
          </div>
          <label className="file-drop">
            Subir un archivo CSV editado
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => importCsv(event.target.files?.[0])}
            />
          </label>
          {importName && (
            <>
              <div className="section-heading">
                <p>
                  <strong>Archivo abierto:</strong> {importName}
                </p>
                <div className="button-row">
                  <button
                    className="secondary-button"
                    onClick={() =>
                      downloadCsv(`editado-${importName}`, importRows)
                    }
                  >
                    Descargar para Excel
                  </button>
                  <button
                    className="primary-button"
                    disabled={importWarnings.length > 0}
                    onClick={saveImported}
                  >
                    Guardar en Google Sheets
                  </button>
                </div>
              </div>
              {importWarnings.length > 0 && (
                <div className="import-warnings">
                  <strong>Revisión necesaria</strong>
                  <ul>
                    {importWarnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="table-wrapper import-table">
                <table>
                  <thead>
                    <tr>
                      <th>Acciones</th>
                      {Object.keys(importRows[0] ?? {}).map((key) => (
                        <th key={key}>{key}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {importRows.slice(0, 30).map((row, index) => (
                      <tr key={index}>
                        <td>
                          <button
                            type="button"
                            className="mini-action"
                            onClick={() => duplicateImportRow(index)}
                          >
                            Duplicar y adaptar
                          </button>
                        </td>
                        {Object.keys(importRows[0] ?? {}).map((key) => (
                          <td key={key}>
                            <textarea
                              value={row[key] ?? ""}
                              rows={
                                key === "prompt" ||
                                key === "feedback" ||
                                key === "summary"
                                  ? 4
                                  : 2
                              }
                              onChange={(event) =>
                                updateImportCell(index, key, event.target.value)
                              }
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p>
                <small>
                  La vista permite editar las primeras 30 filas. Para cambios
                  masivos, utiliza directamente las hojas de Google Sheets.
                </small>
              </p>
            </>
          )}
        </section>
      )}

      <button className="text-button teacher-back" onClick={onExit}>
        Volver a la portada
      </button>
    </main>
  );
}
